import {
  personRailPayment,
  shortVendorName,
} from "./analysis/statement-contact.js"
import { deriveVendorLabel } from "./analysis/vendor-label.js"
import type { PortalAnswerValue } from "./portal-item-answers.js"
import {
  formatWorkflowAmount,
  formatWorkflowDate,
} from "./workflows/figures.js"

/**
 * Questions about transactions (PRD: portal-transaction-questions). A firm
 * asks a client what the rows without a category were for; the client picks
 * one of the firm's categories or leaves a note; the firm accepts the answer
 * before any category changes.
 *
 * Pure: no IO, no clock. The repository and the handlers call these so the
 * rules live in one place.
 */

/** One "Ask the client" adds at most this many questions, largest first. */
export const transactionQuestionAskMax = 25

/** A new questions request is due this many days after it is sent. */
export const transactionQuestionDueDays = 7

/** The title of the request "Ask the client" starts when none is open. */
export const transactionQuestionRequestTitle =
  "Questions about your transactions"

/** The portal shows this many rows under a question before "Show all". */
export const transactionQuestionRowsShown = 10

export const transactionQuestionClosedReasons = [
  "accepted",
  "nothing_left",
  "request_closed",
] as const

export type TransactionQuestionClosedReason =
  (typeof transactionQuestionClosedReasons)[number]

/** Why the generic item routes refuse the kind: only the ask makes one. */
export const transactionQuestionAskOnlyMessage =
  "Questions about transactions start from Ask the client."

/** Answers a client can give in one tap. Each is saved as the note. */
export const transactionQuestionQuickAnswers = [
  "It was personal",
  "I'm not sure",
] as const

/**
 * An answer counts with a category or a note that is not blank. A category
 * with an empty note is the common case; a note alone covers "I'm not sure".
 */
export const isTransactionQuestionAnswerValid = (answer: {
  value: PortalAnswerValue
  categoryId?: string | null
}): boolean =>
  typeof answer.value === "string" &&
  answer.value.length <= 2000 &&
  (Boolean(answer.categoryId) || answer.value.trim().length > 0)

const monthYear = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
})

/** Legal endings a bank's registered name carries and a client never says:
    "JPMorgan Chase Bank, N.A." is "JPMorgan Chase" to them. */
const legalSuffix =
  /,?\s+(?:N\.?\s?A\.?|National Association|Inc\.?|LLC|Corp\.?|Corporation|F\.?S\.?B\.?)$/i

/**
 * A bank's name as a client says it: legal endings off, and the word
 * "Bank" before one of them too. "First National Bank" keeps its "Bank",
 * since nothing legal followed it.
 */
export const shortInstitutionName = (name: string): string => {
  let short = name.trim()
  let legal = false
  while (legalSuffix.test(short)) {
    short = short.replace(legalSuffix, "").trim()
    legal = true
  }
  if (legal) short = short.replace(/\s+Bank$/i, "").trim()
  return short || name.trim()
}

/**
 * What a statement is called on a question: the account's own name, else the
 * institution and last four, with the month the statement ends. A statement
 * with no account falls back to its file name.
 */
export const transactionQuestionStatementLabel = (input: {
  accountDisplayName: string | null
  institutionName: string | null
  accountLast4: string | null
  periodEnd: string | null
  fileName: string | null
}): string => {
  const account =
    input.accountDisplayName?.trim() ||
    [
      // "ending 4471", as the missing-statement asks and emails say it.
      input.institutionName?.trim()
        ? shortInstitutionName(input.institutionName)
        : input.accountLast4
          ? "Account"
          : null,
      input.accountLast4 ? `ending ${input.accountLast4}` : null,
    ]
      .filter(Boolean)
      .join(" ")
  const periodEnd = input.periodEnd
    ? new Date(`${input.periodEnd.slice(0, 10)}T12:00:00.000Z`)
    : null
  const month =
    periodEnd && !Number.isNaN(periodEnd.getTime())
      ? monthYear.format(periodEnd)
      : null
  if (account && month) return `${account}, ${month}`
  return account || input.fileName?.trim() || "your statement"
}

/**
 * The vendor as a question names it, the same short name the books use
 * (R5-U10): the Review screen's label cut before the reference numbers bank
 * text carries ("Gusto Payroll Net Pay 8 Employees" is "Gusto Payroll Net
 * Pay"), without a reference word left at the end ("Inv"), at four words,
 * and with bank shorthand spelled out. The first two words always stay, so
 * "7 Eleven" keeps its number. A payment app names the person: "Zelle to
 * Reyes Tom".
 */
export const transactionQuestionVendorLabel = (
  counterpartyKey: string
): string => {
  const payment = personRailPayment(counterpartyKey)
  const person = payment ? shortVendorName(payment.person) : null
  if (payment && person)
    return `${payment.rail} ${payment.direction} ${person}`
  return (
    shortVendorName(counterpartyKey) ??
    deriveVendorLabel(counterpartyKey).replace(/…$/, "")
  )
}

/**
 * The item name the client and the firm see: "<vendor> on <statement>", or,
 * for a row with no vendor, "<amount> paid on <date>, <statement>" ("received"
 * for money in).
 */
export const transactionQuestionItemName = (input: {
  counterpartyKey: string
  statementLabel: string
  /** The vendor's whole Review-screen label, for when the short one is
      another vendor's. */
  full?: boolean
  row: {
    amount: string | null
    currency: string | null
    recordDate: string | null
  } | null
}): string => {
  if (input.counterpartyKey.length > 0 || !input.row) {
    const vendor = input.full
      ? deriveVendorLabel(input.counterpartyKey)
      : transactionQuestionVendorLabel(input.counterpartyKey)
    return `${vendor} on ${input.statementLabel}`
  }
  const amount = input.row.amount
    ? formatWorkflowAmount(
        input.row.amount.replace(/^-/, ""),
        input.row.currency
      )
    : null
  // Money out was paid, money in was received: the name says which.
  const flow = input.row.amount?.startsWith("-") ? "paid" : "received"
  const date = formatWorkflowDate(input.row.recordDate)
  const lead = [amount ? `${amount} ${flow}` : null, date ? `on ${date}` : null]
    .filter(Boolean)
    .join(" ")
  // The date already says the month, so the statement's own ", June 2026"
  // is left off rather than said twice (R5-U12).
  const statement = date
    ? input.statementLabel.replace(/, [A-Z][a-z]+ \d{4}$/, "")
    : input.statementLabel
  return `${lead || "A transaction"}, ${statement}`
}

/**
 * Item names are unique within a request. A name another vendor already
 * holds (two "Check Deposit" keys cut to the same words) falls back to the
 * vendor's full label. The same vendor asked about again on the same
 * statement (the first question closed with rows left) is "<name>, more
 * rows". Any clash left, such as two equal charges on one day, gets " (2)",
 * then " (3)", so each still gets its question.
 *
 * `taken` maps each name on the request to the vendor question holding it
 * (`vendorKey`, "<document>:<vendor key>"), or null for any other item.
 */
export const uniqueTransactionQuestionItemName = (
  names: { short: string; full: string },
  taken: ReadonlyMap<string, string | null>,
  vendorKey: string | null
): string => {
  if (!taken.has(names.short)) return names.short
  const askedAgain = vendorKey !== null && taken.get(names.short) === vendorKey
  const base = askedAgain ? `${names.short}, more rows` : names.full
  if (!taken.has(base)) return base
  let suffix = 2
  while (taken.has(`${base} (${suffix})`)) suffix += 1
  return `${base} (${suffix})`
}
